<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('shippers.shippersLabel') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/ustawienia/spedytorzy/nowy')">
          <i class="material-icons">add</i>
          {{ $t('shippers.addNew') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'shippers'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('shippers.shipperName') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.code') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('price.prices') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(shipper, index) of shippers"
            :key="'shipper-' + index">
            <td class="align-middle">
              {{ shipper.name }}
            </td>
            <td class="align-middle">
              {{ shipper.code }}
            </td>
            <td class="align-middle">
              {{ showPricesCount(shipper.shipper_prices.length) }}
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/ustawienia/spedytorzy/edycja/' + shipper.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeShipper(shipper.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="shippers.length === 0">
            <td
              colspan="4"
              class="text-center">
              {{ $t('shippers.noShippersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'shippers',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies;
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('shippers.shippersSearchPlaceholder'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        }
      ];
    }
  },
  data () {
    return {
      shippers: [],
      currencies: [],
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/shippers/items?page=' + this.currentPage,
        listField: 'shippers',
        params: {
          with: ['shipperPrices'],
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    removeShipper (shipperID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/shippers/delete/',
        id: shipperID,
        successTitle: this.$t('shippers.deleteSuccessTitle'),
        successText: this.$t('shippers.deleteSuccessText'),
        confirmTitle: this.$t('shippers.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('shippers.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    showPricesCount (count) {
      if (count === 0) {
        return this.$t('shippers.noPrices');
      }

      if (count === 1) {
        return `1 ${this.$t('shippers.price')}`;
      }

      if ((count >= 2 && count <= 4) || (count % 100 >= 22 && count % 100 <= 24)) {
        return `${count} ${this.$t('shippers.prices')}`;
      }

      return `${count} ${this.$t('shippers.prices')}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.shipper {
  &-price {
    display: block;
    font-size: 12px;
  }
}
</style>
